import { Component } from 'react';
import type { ErrorInfo, ReactNode } from 'react';

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
}

class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
    };
  }

  static getDerivedStateFromError(error: Error): Partial<State> {
    // Update state so the next render will show the fallback UI
    return {
      hasError: true,
      error,
    };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    // Log error details to console
    console.error('ErrorBoundary caught an error:', error);
    console.error('Error info:', errorInfo);
    console.error('Component stack:', errorInfo.componentStack);

    // Update state with error info
    this.setState({
      errorInfo,
    });

    // Optional: Send error to error reporting service
    // this.logErrorToService(error, errorInfo);
  }

  // Optional method for error reporting service integration
  // Uncomment and implement when integrating with error reporting service
  // private logErrorToService(error: Error, errorInfo: ErrorInfo): void {
  //   // Example: Send to Sentry, LogRocket, or custom service
  //   // if (window.errorReportingService) {
  //   //   window.errorReportingService.logError({
  //   //     error: error.toString(),
  //   //     stack: error.stack,
  //   //     componentStack: errorInfo.componentStack,
  //   //     timestamp: new Date().toISOString(),
  //   //   });
  //   // }
  //   console.log('Error logged to service:', { error, errorInfo });
  // }

  handleReload = (): void => {
    // Reload the page to recover from error
    window.location.reload();
  };

  render(): ReactNode {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen w-full bg-zinc-900 flex items-center justify-center p-4">
          <div className="max-w-2xl w-full bg-zinc-800 rounded-lg border border-zinc-700 p-8">
            {/* Error Icon */}
            <div className="flex justify-center mb-6">
              <div className="w-16 h-16 bg-red-500/20 rounded-full flex items-center justify-center">
                <svg
                  className="w-8 h-8 text-red-500"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                  />
                </svg>
              </div>
            </div>

            {/* Error Title */}
            <h1 className="text-2xl font-bold text-white text-center mb-4">
              Something went wrong
            </h1>

            {/* Error Message */}
            <p className="text-zinc-400 text-center mb-6">
              The application encountered an unexpected error. Please try reloading the page.
            </p>

            {/* Error Details (Development) */}
            {this.state.error && (
              <div className="mb-6 bg-zinc-900 rounded p-4 border border-zinc-700">
                <p className="text-sm font-mono text-red-400 mb-2">
                  {this.state.error.toString()}
                </p>
                {this.state.error.stack && (
                  <details className="mt-2">
                    <summary className="text-xs text-zinc-500 cursor-pointer hover:text-zinc-400">
                      Stack trace
                    </summary>
                    <pre className="text-xs text-zinc-500 mt-2 overflow-x-auto">
                      {this.state.error.stack}
                    </pre>
                  </details>
                )}
                {this.state.errorInfo?.componentStack && (
                  <details className="mt-2">
                    <summary className="text-xs text-zinc-500 cursor-pointer hover:text-zinc-400">
                      Component stack
                    </summary>
                    <pre className="text-xs text-zinc-500 mt-2 overflow-x-auto">
                      {this.state.errorInfo.componentStack}
                    </pre>
                  </details>
                )}
              </div>
            )}

            {/* Action Buttons */}
            <div className="flex gap-4 justify-center">
              <button
                onClick={this.handleReload}
                className="px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium transition-colors"
              >
                Reload Page
              </button>
              <button
                onClick={() => this.setState({ hasError: false, error: null, errorInfo: null })}
                className="px-6 py-3 bg-zinc-700 hover:bg-zinc-600 text-white rounded-lg font-medium transition-colors"
              >
                Try Again
              </button>
            </div>

            {/* Help Text */}
            <p className="text-xs text-zinc-500 text-center mt-6">
              If this problem persists, please contact support or check the browser console for more details.
            </p>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
